/**
 * Product page behaviour: variant selection, add-to-cart, media gallery.
 *
 * Variant data comes from a JSON script tag rendered by the section, so the
 * picker resolves options without a network round trip.
 */

/* ------------------------------------------------------------------ *
 * <variant-picker> — swatches, dropdowns and pills all post here
 * ------------------------------------------------------------------ */

class VariantPicker extends HTMLElement {
  connectedCallback() {
    this.form = this.closest('form') || document.getElementById(this.dataset.form);
    this.idInput = this.form ? this.form.querySelector('[name="id"]') : null;
    this.variants = this.parseVariants();

    this.addEventListener('change', () => this.onVariantChange());
    // Resolve once on load so a deep link with ?variant= lands consistent.
    this.onVariantChange({ initial: true });
  }

  parseVariants() {
    const script = this.querySelector('[data-variant-json]');
    if (!script) return [];
    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      return [];
    }
  }

  /** Selected option values, in option order. */
  get selectedOptions() {
    return Array.from(this.querySelectorAll('[data-option-index]'))
      .filter((input) => (input.tagName === 'SELECT' ? true : input.checked))
      .sort((a, b) => Number(a.dataset.optionIndex) - Number(b.dataset.optionIndex))
      .map((input) => input.value);
  }

  findVariant() {
    const selected = this.selectedOptions;
    return this.variants.find((variant) =>
      variant.options.every((option, index) => option === selected[index])
    );
  }

  onVariantChange(context) {
    const variant = this.findVariant();
    this.updateAvailability();

    if (!variant) {
      this.setUnavailable();
      return;
    }

    if (this.idInput) this.idInput.value = variant.id;
    this.updateUrl(variant, context);
    this.updatePrice(variant);
    this.updateButton(variant);
    this.updateMedia(variant);
    this.updateSelectedLabels();

    this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant: variant } }));
  }

  /** Greys out option values that produce no purchasable combination. */
  updateAvailability() {
    const available = new Map();
    this.variants.forEach((variant) => {
      variant.options.forEach((value, index) => {
        const key = index + ':' + value;
        available.set(key, available.get(key) || variant.available);
      });
    });

    this.querySelectorAll('[data-option-index]').forEach((input) => {
      const key = input.dataset.optionIndex + ':' + input.value;
      const isAvailable = available.get(key);
      const wrapper = input.closest('[data-option-value]') || input;
      wrapper.toggleAttribute('data-unavailable', isAvailable === false);
      if (isAvailable === undefined) wrapper.setAttribute('data-unavailable', '');
    });
  }

  updateSelectedLabels() {
    this.querySelectorAll('[data-selected-value]').forEach((el) => {
      const index = Number(el.dataset.selectedValue);
      el.textContent = this.selectedOptions[index] || '';
    });
  }

  updateUrl(variant, context) {
    if (context && context.initial) return;
    if (this.dataset.updateUrl === 'false') return;
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url.toString());
  }

  updatePrice(variant) {
    const scope = this.dataset.priceTarget ? document.querySelector(this.dataset.priceTarget) : document;
    const price = scope.querySelector('[data-product-price]');
    const compare = scope.querySelector('[data-product-compare-price]');

    if (price) price.innerHTML = Theme.formatMoney(variant.price);
    if (compare) {
      const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
      compare.innerHTML = onSale ? Theme.formatMoney(variant.compare_at_price) : '';
      compare.hidden = !onSale;
    }

    const sku = scope.querySelector('[data-product-sku]');
    if (sku) sku.textContent = variant.sku || '';
  }

  updateButton(variant) {
    if (!this.form) return;
    const button = this.form.querySelector('[data-add-to-cart]');
    if (!button) return;
    const label = button.querySelector('[data-add-to-cart-text]') || button;
    button.disabled = !variant.available;
    label.textContent = variant.available
      ? Theme.strings.addToCart || 'Add to cart'
      : Theme.strings.soldOut || 'Sold out';
  }

  updateMedia(variant) {
    if (!variant.featured_media) return;
    const gallery = document.querySelector('media-gallery');
    if (gallery) gallery.setActiveMedia(variant.featured_media.id);
  }

  setUnavailable() {
    if (!this.form) return;
    const button = this.form.querySelector('[data-add-to-cart]');
    if (!button) return;
    const label = button.querySelector('[data-add-to-cart-text]') || button;
    button.disabled = true;
    label.textContent = Theme.strings.unavailable || 'Unavailable';
  }
}
customElements.define('variant-picker', VariantPicker);

/* ------------------------------------------------------------------ *
 * <product-form> — AJAX add to cart with inline error reporting
 * ------------------------------------------------------------------ */

class ProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    if (!this.form) return;
    this.button = this.querySelector('[data-add-to-cart]');
    this.errorTarget = this.querySelector('[data-form-error]');

    this.form.addEventListener('submit', (event) => this.onSubmit(event));
  }

  async onSubmit(event) {
    if (this.dataset.ajax === 'false') return;
    event.preventDefault();
    if (this.button && this.button.getAttribute('aria-disabled') === 'true') return;

    this.setLoading(true);
    this.clearError();

    try {
      const formData = new FormData(this.form);
      const items = [
        {
          id: Number(formData.get('id')),
          quantity: Number(formData.get('quantity') || 1),
          properties: this.collectProperties(formData),
        },
      ];
      if (formData.get('selling_plan')) items[0].selling_plan = Number(formData.get('selling_plan'));

      const data = await CartAPI.add(items);
      document.dispatchEvent(new CustomEvent('cart:render', { detail: data }));
      document.dispatchEvent(new CustomEvent('cart:added', { detail: data }));
    } catch (error) {
      this.showError(error.message);
    } finally {
      this.setLoading(false);
    }
  }

  collectProperties(formData) {
    const properties = {};
    formData.forEach((value, key) => {
      const match = key.match(/^properties\[(.+)\]$/);
      if (match && String(value).trim() !== '') properties[match[1]] = value;
    });
    return Object.keys(properties).length ? properties : undefined;
  }

  setLoading(state) {
    if (!this.button) return;
    this.button.setAttribute('aria-disabled', String(state));
    this.button.toggleAttribute('data-loading', state);
  }

  clearError() {
    if (!this.errorTarget) return;
    this.errorTarget.textContent = '';
    this.errorTarget.hidden = true;
  }

  showError(message) {
    if (!this.errorTarget) return;
    this.errorTarget.textContent = message;
    this.errorTarget.hidden = false;
  }
}
customElements.define('product-form', ProductForm);

/* ------------------------------------------------------------------ *
 * <media-gallery> — thumbnails drive the main stage
 * ------------------------------------------------------------------ */

class MediaGallery extends HTMLElement {
  connectedCallback() {
    this.stage = this.querySelector('[data-gallery-stage]');
    this.addEventListener('click', (event) => {
      const thumb = event.target.closest('[data-media-id]');
      if (!thumb || !this.contains(thumb)) return;
      if (thumb.closest('[data-gallery-stage]')) return;
      event.preventDefault();
      this.setActiveMedia(thumb.dataset.mediaId);
    });
  }

  setActiveMedia(mediaId) {
    const id = String(mediaId);

    this.querySelectorAll('[data-gallery-stage] [data-media-id]').forEach((item) => {
      const active = item.dataset.mediaId === id;
      item.toggleAttribute('data-active', active);
      item.hidden = !active && this.dataset.layout === 'single';
    });

    this.querySelectorAll('[data-gallery-thumbs] [data-media-id]').forEach((thumb) => {
      const active = thumb.dataset.mediaId === id;
      thumb.toggleAttribute('data-active', active);
      thumb.setAttribute('aria-current', active ? 'true' : 'false');
    });

    // Stacked and grid layouts scroll instead of swapping.
    if (this.dataset.layout !== 'single') {
      const target = this.querySelector('[data-gallery-stage] [data-media-id="' + id + '"]');
      if (target) target.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  }
}
customElements.define('media-gallery', MediaGallery);
